import { useList, useCustom, useCustomMutation } from '@refinedev/core';
import { useState, useMemo, useCallback } from 'react';
import {
  Gamepad2,
  Users,
  Clock,
  Eye,
  Filter,
  User,
  Ban,
  UserX,
  XCircle,
  Play,
  Trophy,
  RefreshCw,
  Lock,
  Globe,
  History,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/components/ui/tabs';
import { DataTable, type Column, type SortState, type BulkAction } from '@/components/tables';
import {
  type LobbyWithStats,
  type LobbyDetails,
  type LobbyPlayer,
  type SessionWithStats,
  type SessionDetails,
  type GameMove,
  type LobbyStatus,
  LOBBY_STATUS_LABELS,
  LOBBY_STATUS_COLORS,
  SESSION_STATUS_LABELS,
  SESSION_STATUS_COLORS,
  PLAYER_STATUS_LABELS,
  PLAYER_STATUS_COLORS,
  GAME_TYPE_LABELS,
} from './game-types';

function formatDate(dateString: string | null) {
  if (!dateString) {return '—';}
  return new Date(dateString).toLocaleDateString('ru-RU', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function getUserName(user: { firstName?: string | null; lastName?: string | null; username: string | null; id?: number }): string {
  const name = [user.firstName, user.lastName].filter(Boolean).join(' ');
  if (name) {return name;}
  if (user.username) {return `@${user.username}`;}
  return user.id ? `Пользователь #${user.id}` : 'Неизвестный';
}

function PlayersPanel({
  players,
  onKick,
  onBan,
}: {
  players: LobbyPlayer[];
  onKick: (player: LobbyPlayer) => void;
  onBan: (player: LobbyPlayer) => void;
}) {
  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-sm font-medium flex items-center gap-2">
          <Users className="h-4 w-4" />
          Игроки ({players.length})
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-2">
        {players.length === 0 ? (
          <p className="text-sm text-muted-foreground">Нет игроков</p>
        ) : (
          players.map((player) => (
            <div
              key={player.id}
              className="flex items-center justify-between p-2 rounded-lg bg-muted/50"
            >
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                  <User className="h-4 w-4 text-primary" />
                </div>
                <div>
                  <div className="text-sm font-medium">{getUserName(player.user)}</div>
                  <div className="flex items-center gap-1">
                    <Badge
                      variant={PLAYER_STATUS_COLORS[player.status]}
                      className="text-xs"
                    >
                      {PLAYER_STATUS_LABELS[player.status]}
                    </Badge>
                    {player.isReady && (
                      <Badge variant="default" className="text-xs bg-green-500">
                        Готов
                      </Badge>
                    )}
                    <span className="text-xs text-muted-foreground">
                      Очки: {player.score}
                    </span>
                  </div>
                  {player.kickReason && (
                    <p className="text-xs text-destructive mt-1">
                      Причина: {player.kickReason}
                    </p>
                  )}
                </div>
              </div>
              {(player.status === 'WAITING' || player.status === 'READY' || player.status === 'PLAYING') && (
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="ghost" size="sm">
                      •••
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-48" align="end">
                    <div className="space-y-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="w-full justify-start text-orange-600"
                        onClick={() => onKick(player)}
                      >
                        <UserX className="h-4 w-4 mr-2" />
                        Выгнать
                      </Button>
                      <Separator />
                      <Button
                        variant="ghost"
                        size="sm"
                        className="w-full justify-start text-destructive"
                        onClick={() => onBan(player)}
                      >
                        <Ban className="h-4 w-4 mr-2" />
                        Забанить в играх
                      </Button>
                    </div>
                  </PopoverContent>
                </Popover>
              )}
            </div>
          ))
        )}
      </CardContent>
    </Card>
  );
}

function MovesPanel({ moves }: { moves: GameMove[] }) {
  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-sm font-medium flex items-center gap-2">
          <History className="h-4 w-4" />
          История ходов ({moves.length})
        </CardTitle>
      </CardHeader>
      <CardContent>
        <ScrollArea className="h-[300px]">
          {moves.length === 0 ? (
            <p className="text-sm text-muted-foreground">Нет ходов</p>
          ) : (
            <div className="space-y-2">
              {moves.map((move) => (
                <div key={move.id} className="p-2 rounded-lg bg-muted/50 text-sm">
                  <div className="flex items-center justify-between">
                    <span className="font-medium">
                      {move.user ? getUserName(move.user) : `User #${move.userId}`}
                    </span>
                    <span className="text-xs text-muted-foreground">
                      {formatDate(move.createdAt)}
                    </span>
                  </div>
                  <div className="text-xs text-muted-foreground mt-1">
                    Тип: {move.moveType}
                  </div>
                  {move.moveData && Object.keys(move.moveData).length > 0 && (
                    <pre className="text-xs mt-1 p-1 bg-background rounded overflow-auto">
                      {JSON.stringify(move.moveData, null, 2)}
                    </pre>
                  )}
                  {move.result && (
                    <div className="mt-1 text-xs text-green-600">
                      Результат: {JSON.stringify(move.result)}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </ScrollArea>
      </CardContent>
    </Card>
  );
}

function LobbyDetailsSheet({
  lobbyId,
  open,
  onClose,
}: {
  lobbyId: number | null;
  open: boolean;
  onClose: () => void;
}) {
  const [actionDialog, setActionDialog] = useState<{
    type: 'close' | 'kick' | 'ban';
    target?: LobbyPlayer;
  } | null>(null);
  const [reason, setReason] = useState('');
  const [duration, setDuration] = useState('0');

  const lobbyResult = useCustom<{ data: LobbyDetails }>({
    url: `/admin/game-moderation/lobbies/${lobbyId}`,
    method: 'get',
    queryOptions: {
      enabled: !!lobbyId,
    },
  });

  const lobbyData = lobbyResult.query.data;
  const refetch = lobbyResult.query.refetch;
  const isLoading = lobbyResult.query.isLoading;
  const lobby = lobbyData?.data?.data;

  const closeMutation = useCustomMutation();
  const kickMutation = useCustomMutation();
  const banMutation = useCustomMutation();

  const handleAction = useCallback(() => {
    if (!actionDialog || !lobbyId) {return;}

    const onSuccess = () => {
      setActionDialog(null);
      setReason('');
      refetch();
    };

    switch (actionDialog.type) {
      case 'close':
        closeMutation.mutate({
          url: `/admin/game-moderation/lobbies/${lobbyId}/close`,
          method: 'post',
          values: { reason },
        }, { onSuccess });
        break;
      case 'kick':
        if (actionDialog.target) {
          kickMutation.mutate({
            url: `/admin/game-moderation/lobbies/${lobbyId}/players/${actionDialog.target.userId}/kick`,
            method: 'post',
            values: { reason },
          }, { onSuccess });
        }
        break;
      case 'ban':
        if (actionDialog.target) {
          banMutation.mutate({
            url: `/admin/game-moderation/users/${actionDialog.target.userId}/ban`,
            method: 'post',
            values: {
              reason,
              durationMinutes: duration !== '0' ? parseInt(duration) : undefined,
            },
          }, { onSuccess });
        }
        break;
    }
  }, [actionDialog, lobbyId, reason, duration, closeMutation, kickMutation, banMutation, refetch]);

  const isActionPending =
    closeMutation.mutation.isPending ||
    kickMutation.mutation.isPending ||
    banMutation.mutation.isPending;

  if (!lobbyId) {return null;}

  return (
    <>
      <Sheet open={open} onOpenChange={onClose}>
        <SheetContent className="w-[700px] sm:max-w-[700px]">
          <SheetHeader>
            <SheetTitle className="flex items-center gap-2">
              <Gamepad2 className="h-5 w-5" />
              Лобби #{lobbyId}
            </SheetTitle>
            <SheetDescription>
              {lobby && (
                <div className="flex items-center gap-2">
                  <Badge variant={LOBBY_STATUS_COLORS[lobby.status as LobbyStatus]}>
                    {LOBBY_STATUS_LABELS[lobby.status as LobbyStatus]}
                  </Badge>
                  <span className="text-xs">•</span>
                  <span className="text-xs">{GAME_TYPE_LABELS[lobby.game.type] || lobby.game.name}</span>
                </div>
              )}
            </SheetDescription>
          </SheetHeader>

          <div className="p-2 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg">
            <p className="text-xs text-yellow-700 dark:text-yellow-300 flex items-center gap-2">
              <Eye className="h-3 w-3" />
              Вы наблюдаете в скрытом режиме. Игроки не видят, что вы просматриваете лобби.
            </p>
          </div>

          <ScrollArea className="h-[calc(100vh-220px)]">
            {isLoading ? (
              <div className="flex items-center justify-center h-32">
                <RefreshCw className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            ) : (lobby ? (
              <div className="space-y-4 pr-4">
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-sm font-medium">Информация</CardTitle>
                  </CardHeader>
                  <CardContent className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-muted-foreground">Игра:</span>
                      <span className="ml-2 font-medium">{GAME_TYPE_LABELS[lobby.game.type] || lobby.game.name}</span>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Хост:</span>
                      <span className="ml-2 font-medium">{getUserName(lobby.host)}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <span className="text-muted-foreground">Доступ:</span>
                      {lobby.isPrivate ? (
                        <Badge variant="secondary" className="ml-2">
                          <Lock className="h-3 w-3 mr-1" />
                          Приватный
                        </Badge>
                      ) : (
                        <Badge variant="outline" className="ml-2">
                          <Globe className="h-3 w-3 mr-1" />
                          Публичный
                        </Badge>
                      )}
                    </div>
                    <div>
                      <span className="text-muted-foreground">Язык:</span>
                      <span className="ml-2">{lobby.language}</span>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Макс. игроков:</span>
                      <span className="ml-2">{lobby.maxPlayers}</span>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Создано:</span>
                      <span className="ml-2">{formatDate(lobby.createdAt)}</span>
                    </div>
                    {lobby.activeSession && (
                      <div className="col-span-2">
                        <span className="text-muted-foreground">Активная сессия:</span>
                        <Badge variant="default" className="ml-2">
                          #{lobby.activeSession.id}
                        </Badge>
                      </div>
                    )}
                  </CardContent>
                </Card>

                <PlayersPanel
                  players={lobby.players}
                  onKick={(p) => setActionDialog({ type: 'kick', target: p })}
                  onBan={(p) => setActionDialog({ type: 'ban', target: p })}
                />

                {(lobby.status === 'WAITING' || lobby.status === 'STARTING' || lobby.status === 'IN_PROGRESS') && (
                  <Card>
                    <CardHeader className="pb-3">
                      <CardTitle className="text-sm font-medium">Действия</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <Button
                        variant="destructive"
                        size="sm"
                        onClick={() => setActionDialog({ type: 'close' })}
                      >
                        <XCircle className="h-4 w-4 mr-2" />
                        Закрыть лобби
                      </Button>
                    </CardContent>
                  </Card>
                )}
              </div>
            ) : (
              <div className="flex items-center justify-center h-32 text-muted-foreground">
                Лобби не найдено
              </div>
            ))}
          </ScrollArea>
        </SheetContent>
      </Sheet>

      <Dialog open={!!actionDialog} onOpenChange={() => setActionDialog(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {actionDialog?.type === 'close' && 'Закрыть лобби'}
              {actionDialog?.type === 'kick' && 'Выгнать игрока'}
              {actionDialog?.type === 'ban' && 'Забанить в играх'}
            </DialogTitle>
            <DialogDescription>
              {actionDialog?.type === 'close' && 'Лобби будет принудительно закрыто. Все игроки будут отключены.'}
              {actionDialog?.type === 'kick' && 'Игрок будет удалён из лобби.'}
              {actionDialog?.type === 'ban' && 'Пользователь не сможет участвовать в играх.'}
            </DialogDescription>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <div>
              <Label htmlFor="game-reason">Причина *</Label>
              <Textarea
                id="game-reason"
                placeholder="Укажите причину..."
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                className="mt-2"
                rows={3}
              />
            </div>
            {actionDialog?.type === 'ban' && (
              <div>
                <Label htmlFor="game-duration">Длительность бана</Label>
                <Select value={duration} onValueChange={setDuration}>
                  <SelectTrigger id="game-duration" className="mt-2">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="60">1 час</SelectItem>
                    <SelectItem value="1440">24 часа</SelectItem>
                    <SelectItem value="10080">7 дней</SelectItem>
                    <SelectItem value="43200">30 дней</SelectItem>
                    <SelectItem value="0">Бессрочно</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setActionDialog(null)} disabled={isActionPending}>
              Отмена
            </Button>
            <Button
              variant="destructive"
              onClick={handleAction}
              disabled={isActionPending || !reason.trim()}
            >
              {isActionPending ? 'Выполнение...' : 'Подтвердить'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

function SessionDetailsSheet({
  sessionId,
  open,
  onClose,
}: {
  sessionId: number | null;
  open: boolean;
  onClose: () => void;
}) {
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [reason, setReason] = useState('');

  const sessionResult = useCustom<{ data: SessionDetails }>({
    url: `/admin/game-moderation/sessions/${sessionId}`,
    method: 'get',
    queryOptions: {
      enabled: !!sessionId,
    },
  });

  const movesResult = useCustom<{ data: GameMove[] }>({
    url: `/admin/game-moderation/sessions/${sessionId}/moves`,
    method: 'get',
    queryOptions: {
      enabled: !!sessionId,
    },
  });

  const sessionData = sessionResult.query.data;
  const refetch = sessionResult.query.refetch;
  const isLoading = sessionResult.query.isLoading;
  const movesData = movesResult.query.data;
  const isLoadingMoves = movesResult.query.isLoading;
  const session: SessionDetails | undefined = sessionData?.data?.data;
  const moves: GameMove[] = movesData?.data?.data ?? [];

  const cancelMutation = useCustomMutation();

  const handleCancel = useCallback(() => {
    if (!sessionId || !reason.trim()) {return;}

    cancelMutation.mutate({
      url: `/admin/game-moderation/sessions/${sessionId}/cancel`,
      method: 'post',
      values: { reason },
    }, {
      onSuccess: () => {
        setCancelDialogOpen(false);
        setReason('');
        refetch();
      },
    });
  }, [sessionId, reason, cancelMutation, refetch]);

  if (!sessionId) {return null;}

  return (
    <>
      <Sheet open={open} onOpenChange={onClose}>
        <SheetContent className="w-[700px] sm:max-w-[700px]">
          <SheetHeader>
            <SheetTitle className="flex items-center gap-2">
              <Play className="h-5 w-5" />
              Сессия #{sessionId}
            </SheetTitle>
            <SheetDescription>
              {session && (
                <div className="flex items-center gap-2">
                  <Badge variant={SESSION_STATUS_COLORS[session.status]}>
                    {SESSION_STATUS_LABELS[session.status]}
                  </Badge>
                  <span className="text-xs">•</span>
                  <span className="text-xs">{GAME_TYPE_LABELS[session.game.type] || session.game.name}</span>
                </div>
              )}
            </SheetDescription>
          </SheetHeader>

          <div className="p-2 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg">
            <p className="text-xs text-yellow-700 dark:text-yellow-300 flex items-center gap-2">
              <Eye className="h-3 w-3" />
              Вы наблюдаете в скрытом режиме. Для GuessNumber число скрыто до завершения игры.
            </p>
          </div>

          <ScrollArea className="h-[calc(100vh-220px)]">
            {isLoading ? (
              <div className="flex items-center justify-center h-32">
                <RefreshCw className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            ) : (session ? (
              <div className="space-y-4 pr-4">
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-sm font-medium">Информация</CardTitle>
                  </CardHeader>
                  <CardContent className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-muted-foreground">Игра:</span>
                      <span className="ml-2 font-medium">{GAME_TYPE_LABELS[session.game.type] || session.game.name}</span>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Ход №:</span>
                      <span className="ml-2">{session.turnNumber}</span>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Начало:</span>
                      <span className="ml-2">{formatDate(session.startedAt)}</span>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Завершение:</span>
                      <span className="ml-2">{formatDate(session.finishedAt)}</span>
                    </div>
                    {session.winner && (
                      <div className="col-span-2 flex items-center gap-2">
                        <Trophy className="h-4 w-4 text-yellow-500" />
                        <span className="text-muted-foreground">Победитель:</span>
                        <span className="font-medium">{getUserName(session.winner)}</span>
                      </div>
                    )}
                    {session.lobbyId && (
                      <div>
                        <span className="text-muted-foreground">Лобби:</span>
                        <Badge variant="outline" className="ml-2">#{session.lobbyId}</Badge>
                      </div>
                    )}
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-sm font-medium">Состояние игры</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <pre className="text-xs p-2 bg-muted rounded overflow-auto max-h-[200px]">
                      {JSON.stringify(session.gameState, null, 2)}
                    </pre>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-sm font-medium flex items-center gap-2">
                      <Users className="h-4 w-4" />
                      Игроки ({session.players.length})
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2">
                    {session.players.map((player) => (
                      <div
                        key={player.userId}
                        className="flex items-center justify-between p-2 rounded-lg bg-muted/50"
                      >
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                            <User className="h-4 w-4 text-primary" />
                          </div>
                          <div>
                            <div className="text-sm font-medium">{getUserName(player)}</div>
                            <div className="text-xs text-muted-foreground">
                              Очки: {player.score}
                            </div>
                          </div>
                        </div>
                        {session.winnerId === player.userId && (
                          <Trophy className="h-5 w-5 text-yellow-500" />
                        )}
                        {session.currentTurnUserId === player.userId && (
                          <Badge variant="default">Ходит</Badge>
                        )}
                      </div>
                    ))}
                  </CardContent>
                </Card>

                {!isLoadingMoves && <MovesPanel moves={moves} />}

                {session.status === 'ACTIVE' && (
                  <Card>
                    <CardHeader className="pb-3">
                      <CardTitle className="text-sm font-medium">Действия</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <Button
                        variant="destructive"
                        size="sm"
                        onClick={() => setCancelDialogOpen(true)}
                      >
                        <XCircle className="h-4 w-4 mr-2" />
                        Отменить сессию
                      </Button>
                    </CardContent>
                  </Card>
                )}
              </div>
            ) : (
              <div className="flex items-center justify-center h-32 text-muted-foreground">
                Сессия не найдена
              </div>
            ))}
          </ScrollArea>
        </SheetContent>
      </Sheet>

      <Dialog open={cancelDialogOpen} onOpenChange={setCancelDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Отменить сессию</DialogTitle>
            <DialogDescription>
              Игровая сессия будет отменена. Результаты не будут засчитаны.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Label htmlFor="cancel-reason">Причина *</Label>
            <Textarea
              id="cancel-reason"
              placeholder="Укажите причину отмены..."
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              className="mt-2"
              rows={3}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCancelDialogOpen(false)} disabled={cancelMutation.mutation.isPending}>
              Отмена
            </Button>
            <Button
              variant="destructive"
              onClick={handleCancel}
              disabled={cancelMutation.mutation.isPending || !reason.trim()}
            >
              {cancelMutation.mutation.isPending ? 'Выполнение...' : 'Отменить сессию'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

function getActiveFiltersCount(statusFilter: string): number {
  return statusFilter !== 'all' ? 1 : 0;
}

export function GameModerationTab() {
  const [activeTab, setActiveTab] = useState<'lobbies' | 'sessions'>('lobbies');
  const [lobbyPage, setLobbyPage] = useState(1);
  const [sessionPage, setSessionPage] = useState(1);
  const [lobbySort, setLobbySort] = useState<SortState | undefined>({
    field: 'createdAt',
    order: 'desc',
  });
  const [sessionSort, setSessionSort] = useState<SortState | undefined>({
    field: 'startedAt',
    order: 'desc',
  });
  const [lobbyStatusFilter, setLobbyStatusFilter] = useState<string>('all');
  const [sessionStatusFilter, setSessionStatusFilter] = useState<string>('all');
  const [selectedLobbyId, setSelectedLobbyId] = useState<number | null>(null);
  const [selectedSessionId, setSelectedSessionId] = useState<number | null>(null);
  const pageSize = 20;

  const lobbyFiltersCount = getActiveFiltersCount(lobbyStatusFilter);
  const sessionFiltersCount = getActiveFiltersCount(sessionStatusFilter);

  const lobbyFilters = useMemo(() => {
    const result: Array<{ field: string; operator: 'eq'; value: string }> = [];
    if (lobbyStatusFilter !== 'all') {
      result.push({ field: 'status', operator: 'eq', value: lobbyStatusFilter });
    }
    return result;
  }, [lobbyStatusFilter]);

  const sessionFilters = useMemo(() => {
    const result: Array<{ field: string; operator: 'eq'; value: string }> = [];
    if (sessionStatusFilter !== 'all') {
      result.push({ field: 'status', operator: 'eq', value: sessionStatusFilter });
    }
    return result;
  }, [sessionStatusFilter]);

  const lobbyList = useList<LobbyWithStats>({
    resource: 'game-moderation/lobbies',
    pagination: { currentPage: lobbyPage, pageSize, mode: 'server' },
    filters: lobbyFilters,
    sorters: lobbySort ? [{ field: lobbySort.field, order: lobbySort.order }] : [],
  });

  const sessionList = useList<SessionWithStats>({
    resource: 'game-moderation/sessions',
    pagination: { currentPage: sessionPage, pageSize, mode: 'server' },
    filters: sessionFilters,
    sorters: sessionSort ? [{ field: sessionSort.field, order: sessionSort.order }] : [],
  });

  const lobbies = lobbyList.result.data ?? [];
  const lobbyTotal = lobbyList.result.total ?? 0;
  const isLoadingLobbies = lobbyList.query.isLoading;

  const sessions = sessionList.result.data ?? [];
  const sessionTotal = sessionList.result.total ?? 0;
  const isLoadingSessions = sessionList.query.isLoading;

  const lobbyBulkActions: BulkAction<LobbyWithStats>[] = [
    {
      label: 'Открыть лобби',
      icon: <Eye className="h-4 w-4" />,
      onClick: (items) => {
        if (items.length === 1) {
          setSelectedLobbyId(items[0].id);
        }
      },
      showOnlyWhenSelected: true,
    },
  ];

  const sessionBulkActions: BulkAction<SessionWithStats>[] = [
    {
      label: 'Открыть сессию',
      icon: <Eye className="h-4 w-4" />,
      onClick: (items) => {
        if (items.length === 1) {
          setSelectedSessionId(items[0].id);
        }
      },
      showOnlyWhenSelected: true,
    },
  ];

  const lobbyColumns: Column<LobbyWithStats>[] = [
    {
      key: 'id',
      header: 'ID',
      width: '70px',
      sortable: true,
      render: (value) => (
        <span className="font-mono text-xs text-muted-foreground">#{String(value)}</span>
      ),
    },
    {
      key: 'gameName',
      header: 'Игра',
      width: '150px',
      render: (_, item) => (
        <Badge variant="outline">
          {GAME_TYPE_LABELS[item.gameType] || item.gameName}
        </Badge>
      ),
    },
    {
      key: 'hostFirstName',
      header: 'Хост',
      width: '180px',
      render: (_, item) => (
        <div className="flex items-center gap-2">
          <div className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center">
            <User className="h-3 w-3 text-primary" />
          </div>
          <span className="text-sm truncate">
            {item.hostFirstName || (item.hostUsername ? `@${item.hostUsername}` : `#${item.hostUserId}`)}
          </span>
        </div>
      ),
    },
    {
      key: 'currentPlayers',
      header: 'Игроки',
      width: '100px',
      render: (_, item) => (
        <div className="flex items-center gap-1">
          <Users className="h-3.5 w-3.5 text-muted-foreground" />
          <span>{item.currentPlayers}/{item.maxPlayers}</span>
        </div>
      ),
    },
    {
      key: 'isPrivate',
      header: 'Доступ',
      width: '100px',
      render: (value) => value ? (
        <Badge variant="secondary">
          <Lock className="h-3 w-3 mr-1" />
          Приват
        </Badge>
      ) : (
        <Badge variant="outline">
          <Globe className="h-3 w-3 mr-1" />
          Открыт
        </Badge>
      ),
    },
    {
      key: 'status',
      header: 'Статус',
      width: '130px',
      sortable: true,
      render: (_, item) => (
        <Badge variant={LOBBY_STATUS_COLORS[item.status]}>
          {LOBBY_STATUS_LABELS[item.status]}
        </Badge>
      ),
    },
    {
      key: 'createdAt',
      header: 'Создано',
      width: '160px',
      sortable: true,
      render: (value) => (
        <div className="flex items-center gap-2 text-sm">
          <Clock className="h-3.5 w-3.5 text-muted-foreground" />
          {formatDate(value as string)}
        </div>
      ),
    },
    {
      key: 'actions',
      header: '',
      width: '60px',
      render: (_, item) => (
        <Button variant="ghost" size="sm" onClick={() => setSelectedLobbyId(item.id)}>
          <Eye className="h-4 w-4" />
        </Button>
      ),
    },
  ];

  const sessionColumns: Column<SessionWithStats>[] = [
    {
      key: 'id',
      header: 'ID',
      width: '70px',
      sortable: true,
      render: (value) => (
        <span className="font-mono text-xs text-muted-foreground">#{String(value)}</span>
      ),
    },
    {
      key: 'gameName',
      header: 'Игра',
      width: '150px',
      render: (_, item) => (
        <Badge variant="outline">
          {GAME_TYPE_LABELS[item.gameType] || item.gameName}
        </Badge>
      ),
    },
    {
      key: 'lobbyId',
      header: 'Лобби',
      width: '80px',
      render: (value) => value ? (
        <Badge variant="secondary">#{String(value)}</Badge>
      ) : (
        <span className="text-muted-foreground">—</span>
      ),
    },
    {
      key: 'turnNumber',
      header: 'Ходов',
      width: '80px',
      render: (_, item) => (
        <span>{item.turnNumber}</span>
      ),
    },
    {
      key: 'movesCount',
      header: 'Действий',
      width: '100px',
      render: (value) => (
        <span>{String(value)}</span>
      ),
    },
    {
      key: 'status',
      header: 'Статус',
      width: '130px',
      sortable: true,
      render: (_, item) => (
        <Badge variant={SESSION_STATUS_COLORS[item.status]}>
          {SESSION_STATUS_LABELS[item.status]}
        </Badge>
      ),
    },
    {
      key: 'startedAt',
      header: 'Начало',
      width: '160px',
      sortable: true,
      render: (value) => (
        <div className="flex items-center gap-2 text-sm">
          <Clock className="h-3.5 w-3.5 text-muted-foreground" />
          {formatDate(value as string)}
        </div>
      ),
    },
    {
      key: 'actions',
      header: '',
      width: '60px',
      render: (_, item) => (
        <Button variant="ghost" size="sm" onClick={() => setSelectedSessionId(item.id)}>
          <Eye className="h-4 w-4" />
        </Button>
      ),
    },
  ];

  return (
    <div className="pt-4">
      <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as 'lobbies' | 'sessions')}>
        <TabsList>
          <TabsTrigger value="lobbies">Лобби</TabsTrigger>
          <TabsTrigger value="sessions">Сессии</TabsTrigger>
        </TabsList>

        <TabsContent value="lobbies" forceMount className="space-y-4 data-[state=inactive]:hidden">
          <div className="flex items-center gap-2">
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" className="gap-2">
                  <Filter className="h-4 w-4" />
                  Фильтры
                  {lobbyFiltersCount > 0 && (
                    <Badge variant="secondary" className="ml-1 px-1.5 py-0.5 text-xs">
                      {lobbyFiltersCount}
                    </Badge>
                  )}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-80" align="start">
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label>Статус</Label>
                    <Select
                      value={lobbyStatusFilter}
                      onValueChange={(v) => {
                        setLobbyStatusFilter(v);
                        setLobbyPage(1);
                      }}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Все</SelectItem>
                        <SelectItem value="WAITING">Ожидание</SelectItem>
                        <SelectItem value="STARTING">Запуск</SelectItem>
                        <SelectItem value="IN_PROGRESS">В игре</SelectItem>
                        <SelectItem value="FINISHED">Завершено</SelectItem>
                        <SelectItem value="CANCELLED">Отменено</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  {lobbyFiltersCount > 0 && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="w-full"
                      onClick={() => {
                        setLobbyStatusFilter('all');
                        setLobbyPage(1);
                      }}
                    >
                      Сбросить
                    </Button>
                  )}
                </div>
              </PopoverContent>
            </Popover>
          </div>

          <DataTable
            columns={lobbyColumns}
            data={lobbies}
            total={lobbyTotal}
            page={lobbyPage}
            pageSize={pageSize}
            isLoading={isLoadingLobbies}
            onPageChange={setLobbyPage}
            onRowClick={(lobby) => setSelectedLobbyId(lobby.id)}
            emptyMessage="Нет лобби"
            selectable
            rowKey="id"
            sort={lobbySort}
            onSortChange={setLobbySort}
            bulkActions={lobbyBulkActions}
          />
        </TabsContent>

        <TabsContent value="sessions" forceMount className="space-y-4 data-[state=inactive]:hidden">
          <div className="flex items-center gap-2">
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" className="gap-2">
                  <Filter className="h-4 w-4" />
                  Фильтры
                  {sessionFiltersCount > 0 && (
                    <Badge variant="secondary" className="ml-1 px-1.5 py-0.5 text-xs">
                      {sessionFiltersCount}
                    </Badge>
                  )}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-80" align="start">
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label>Статус</Label>
                    <Select
                      value={sessionStatusFilter}
                      onValueChange={(v) => {
                        setSessionStatusFilter(v);
                        setSessionPage(1);
                      }}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Все</SelectItem>
                        <SelectItem value="ACTIVE">Активна</SelectItem>
                        <SelectItem value="FINISHED">Завершена</SelectItem>
                        <SelectItem value="TIMEOUT">Тайм-аут</SelectItem>
                        <SelectItem value="CANCELLED">Отменена</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  {sessionFiltersCount > 0 && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="w-full"
                      onClick={() => {
                        setSessionStatusFilter('all');
                        setSessionPage(1);
                      }}
                    >
                      Сбросить
                    </Button>
                  )}
                </div>
              </PopoverContent>
            </Popover>
          </div>

          <DataTable
            columns={sessionColumns}
            data={sessions}
            total={sessionTotal}
            page={sessionPage}
            pageSize={pageSize}
            isLoading={isLoadingSessions}
            onPageChange={setSessionPage}
            onRowClick={(session) => setSelectedSessionId(session.id)}
            emptyMessage="Нет сессий"
            selectable
            rowKey="id"
            sort={sessionSort}
            onSortChange={setSessionSort}
            bulkActions={sessionBulkActions}
          />
        </TabsContent>
      </Tabs>

      <LobbyDetailsSheet
        lobbyId={selectedLobbyId}
        open={!!selectedLobbyId}
        onClose={() => setSelectedLobbyId(null)}
      />
      <SessionDetailsSheet
        sessionId={selectedSessionId}
        open={!!selectedSessionId}
        onClose={() => setSelectedSessionId(null)}
      />
    </div>
  );
}
